import { useState, useEffect, useRef, useCallback } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { input as inputStyle, colors, card } from './styles'

interface SearchSelectProps<T> {
  readonly placeholder?: string
  readonly value: string
  readonly displayValue?: string
  readonly onSearch: (query: string) => Promise<readonly T[]>
  readonly onSelect: (item: T) => void
  readonly renderItem: (item: T) => ReactNode
  readonly keyField: keyof T
  readonly disabled?: boolean
  readonly debounceMs?: number
}

const dropdown: CSSProperties = {
  ...card,
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 100,
  maxHeight: '200px',
  overflow: 'auto',
  padding: 0,
  marginTop: '2px',
}

const itemStyle: CSSProperties = {
  padding: '0.5rem 0.75rem',
  cursor: 'pointer',
  fontSize: '0.875rem',
  borderBottom: `1px solid ${colors.border}`,
}

const itemHover: CSSProperties = {
  ...itemStyle,
  background: colors.bgMuted,
}

export function SearchSelect<T>({
  placeholder = 'Search...',
  value,
  displayValue,
  onSearch,
  onSelect,
  renderItem,
  keyField,
  disabled = false,
  debounceMs = 300,
}: SearchSelectProps<T>) {
  const [query, setQuery] = useState(displayValue ?? '')
  const [prevDisplayValue, setPrevDisplayValue] = useState(displayValue)
  const [results, setResults] = useState<readonly T[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState(-1)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync the local query state when the parent supplies a new displayValue
  // (e.g., after an async lookup "by ID"). Derived state computed during
  // render -- see https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (displayValue !== prevDisplayValue) {
    setPrevDisplayValue(displayValue)
    if (displayValue !== undefined) setQuery(displayValue)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleChange = useCallback(
    (val: string) => {
      setQuery(val)
      if (timerRef.current) clearTimeout(timerRef.current)
      if (val.length < 2) {
        setResults([])
        setIsOpen(false)
        return
      }
      timerRef.current = setTimeout(async () => {
        const items = await onSearch(val)
        setResults(items)
        setIsOpen(items.length > 0)
      }, debounceMs)
    },
    [onSearch, debounceMs],
  )

  const handleSelect = (item: T) => {
    onSelect(item)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        placeholder={placeholder}
        value={query}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => {
          if (results.length > 0) setIsOpen(true)
        }}
        disabled={disabled}
      />
      {value && <input type="hidden" value={value} />}
      {isOpen && results.length > 0 && (
        <div style={dropdown}>
          {results.map((item, i) => (
            <div
              key={String(item[keyField])}
              style={i === hoveredIndex ? itemHover : itemStyle}
              onClick={() => handleSelect(item)}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(-1)}
            >
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
